// Aanvraagformulier (sections/rf-request-form.liquid): fotocheck / compositie-ideeën.
//
// Afspraak met het ontvangstpunt van de CRM-tool (runoff-tools, lib/crm/aanvraag.js):
//   POST https://tools.runoff.studio/api/f/<code>/aanvraag  als multipart/form-data, velden:
//     type        "photo_check" | "composition"
//     name, email, car                (verplicht)
//     phone (bijv. "+31612345678"), notes, newsletter ("1")  (optioneel)
//     locale ("en" | "nl" ...), country, page_url
//     utm_source, utm_medium, utm_campaign, utm_content, utm_term, fbclid (indien bekend)
//     photo_1 .. photo_N              JPEG, langste zijde max. 2400 px (ruim onder 5 MB)
//     website                          honeypot: gevuld = spam, negeren
//     duur                             ms tussen laden en versturen (te snel = bot)
//     sleutel                          uniek per aanvraag: opnieuw versturen maakt geen dubbele
//     cf-turnstile-response            Cloudflare Turnstile (onzichtbare spamcheck)
//   Antwoord: HTTP 2xx (bijv. {"ok":true}) = gelukt; al het andere = foutmelding tonen.
// Na succes publiceert het formulier het Shopify-klantevent "runoff_lead" (voor een
// custom pixel → Meta "Lead").

const MAX_SIDE = 2400;
const JPEG_QUALITY = 0.85;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
const UTM_STORE = 'rf_utm';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Turnstile-script één keer laden (ook met meerdere formulieren op de pagina).
let turnstileLoading;
function loadTurnstile() {
  turnstileLoading ??= new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error('Turnstile niet geladen'));
    document.head.append(script);
  });
  return turnstileLoading;
}

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

// UTM's van de landingspagina bewaren, zodat ze ook na doorklikken meegaan.
function rememberUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = Object.fromEntries(UTM_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
    if (Object.keys(found).length) sessionStorage.setItem(UTM_STORE, JSON.stringify(found));
    return JSON.parse(sessionStorage.getItem(UTM_STORE) || '{}');
  } catch {
    return {};
  }
}

async function shrink(file) {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    return blob || file;
  } catch {
    // Browser kan het formaat niet lezen (bijv. HEIC op desktop): origineel sturen.
    return file;
  }
}

if (!customElements.get('rf-request-form')) {
  customElements.define(
    'rf-request-form',
    class RfRequestForm extends HTMLElement {
      #files = [];

      connectedCallback() {
        this.form = this.querySelector('form');
        this.fileInput = this.querySelector('.rf-request__file');
        this.thumbs = this.querySelector('.rf-request__thumbs');
        this.dropText = this.querySelector('.rf-request__drop-text');
        this.errorBox = this.querySelector('.rf-request__error');
        this.utm = rememberUtm();
        this.startedAt = Date.now();
        this.key = newKey();
        this.setupTurnstile();

        this.fileInput.addEventListener('change', () => this.addFiles(this.fileInput.files));
        this.form.addEventListener('submit', (event) => this.submit(event));
      }

      // Onzichtbare spamcheck: alleen bij twijfel ziet de bezoeker een vinkje.
      async setupTurnstile() {
        const sitekey = this.dataset.turnstileSitekey;
        const box = this.querySelector('.rf-request__turnstile');
        if (!sitekey || !box) return;
        try {
          const turnstile = await loadTurnstile();
          this.turnstileId = turnstile.render(box, {
            sitekey,
            appearance: 'interaction-only',
            language: document.documentElement.lang || 'auto',
            'refresh-expired': 'auto',
            callback: () => this.turnstileWaiter?.(),
            // Fout (bijv. domein niet toegestaan): één melding, niet blijven wachten bij versturen.
            'error-callback': (code) => {
              if (!this.turnstileFailed) console.warn('[rf-request-form] Turnstile', code);
              this.turnstileFailed = true;
              this.turnstileWaiter?.();
              return true;
            },
            retry: 'never',
          });
        } catch (error) {
          console.error('[rf-request-form]', error);
        }
      }

      // Token van Turnstile; wacht er max. 15 s op als de check nog bezig is.
      async turnstileToken() {
        if (this.turnstileId === undefined) return '';
        const now = () => window.turnstile?.getResponse(this.turnstileId) || '';
        if (now() || this.turnstileFailed) return now();
        await new Promise((resolve) => {
          this.turnstileWaiter = resolve;
          setTimeout(resolve, 15000);
        });
        this.turnstileWaiter = null;
        return now();
      }

      get maxPhotos() {
        return Number(this.dataset.maxPhotos) || 3;
      }

      addFiles(list) {
        const incoming = Array.from(list || []).filter((f) => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name));
        const room = this.maxPhotos - this.#files.length;
        if (incoming.length > room) this.showError(this.dataset.msgTooMany);
        else this.showError('');
        this.#files.push(...incoming.slice(0, Math.max(room, 0)));
        this.fileInput.value = '';
        this.renderThumbs();
      }

      renderThumbs() {
        this.thumbs.replaceChildren(
          ...this.#files.map((file, index) => {
            const li = document.createElement('li');
            const img = document.createElement('img');
            img.alt = file.name;
            img.src = URL.createObjectURL(file);
            img.onload = () => URL.revokeObjectURL(img.src);
            img.onerror = () => img.replaceWith(document.createTextNode(file.name));
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'rf-request__thumb-remove';
            remove.setAttribute('aria-label', `✕ ${file.name}`);
            remove.textContent = '✕';
            remove.addEventListener('click', () => {
              this.#files.splice(index, 1);
              this.renderThumbs();
            });
            li.append(img, remove);
            return li;
          })
        );
        this.dropText.textContent = this.#files.length
          ? `${this.#files.length} / ${this.maxPhotos}`
          : this.dropText.dataset.empty;
      }

      showError(message) {
        this.errorBox.hidden = !message;
        this.errorBox.innerHTML = message || '';
      }

      setBusy(busy) {
        const button = this.form.querySelector('[type="submit"]');
        button.disabled = busy;
        this.form.querySelector('.rf-request__submit-label').hidden = busy;
        this.form.querySelector('.rf-request__submit-busy').hidden = !busy;
      }

      async submit(event) {
        event.preventDefault();
        if (!this.form.reportValidity()) return;
        if (this.dataset.photosRequired === 'true' && this.#files.length === 0) {
          this.showError(this.dataset.msgPhotos);
          return;
        }

        const errorHtml = this.querySelector('.rf-request__error-text')?.innerHTML.trim();
        const endpoint = this.dataset.endpoint;
        if (!endpoint) {
          this.showError(errorHtml);
          return;
        }

        this.showError('');
        this.setBusy(true);
        try {
          const data = new FormData(this.form);
          data.set('page_url', window.location.href.split('#')[0]);
          // Telefoon: landcode + nummer zonder voorloop-0 en zonder spaties/streepjes.
          let national = String(data.get('phone_number') || '').replace(/[^\d+]/g, '');
          if (national.startsWith('00')) national = `+${national.slice(2)}`; // 0049... = +49...
          const phone = national.startsWith('+')
            ? national
            : national
            ? `${data.get('phone_country') || ''}${national.replace(/^0+/, '')}`
            : '';
          data.delete('phone_number');
          data.delete('phone_country');
          data.set('phone', phone);
          for (const [key, value] of Object.entries(this.utm)) data.set(key, value);
          data.set('duur', String(Date.now() - this.startedAt));
          data.set('sleutel', this.key);
          data.set('cf-turnstile-response', await this.turnstileToken());
          const photos = await Promise.all(this.#files.map(shrink));
          photos.forEach((blob, i) => {
            const name = `photo-${i + 1}.${blob.type === 'image/jpeg' ? 'jpg' : this.#files[i].name.split('.').pop()}`;
            data.append(`photo_${i + 1}`, blob, name);
          });

          const response = await fetch(endpoint, { method: 'POST', body: data });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          this.key = newKey();

          this.form.hidden = true;
          const success = this.querySelector('.rf-request__success');
          success.hidden = false;
          success.focus();
          try {
            window.Shopify?.analytics?.publish?.('runoff_lead', { type: data.get('type') });
          } catch {}
        } catch (error) {
          console.error('[rf-request-form]', error);
          this.showError(errorHtml);
          // Een token werkt maar één keer: nieuwe check voor de volgende poging.
          if (this.turnstileId !== undefined) {
            this.turnstileFailed = false;
            window.turnstile?.reset(this.turnstileId);
          }
        } finally {
          this.setBusy(false);
        }
      }
    }
  );
}
